import { Box, Checkbox, Flex, FormControl, FormErrorMessage, FormLabel, Radio, RadioGroup, Select, Stack, Text } from '@chakra-ui/react'
import type { PersonAdditionalData, WizardPropertyAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import { QuestionMarkIcon } from '@/components'
import RuianAddressSelector from '@/components/RuianAddressSelector'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import ICOOptions from '@/containers/forms/person/ICOOptions'
import InsureeContact from '@/containers/forms/person/InsureeContact'
import InsureeName from '@/containers/forms/person/InsureeName'
import PersonType from '@/containers/forms/person/PersonType'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelections } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'

export const AdditionalInsuredInfoForm = () => {
  const { product } = useApp()
  const { isDisabled } = useInsuranceParams()

  const { watch, register, getValues, setValue, getFieldState, formState: { errors } } = useFormContext<WizardPropertyAdditionalData>()

  const nameof = useNameof<WizardPropertyAdditionalData>()
  const personPrefix = nameof(x => x.policyHolder.insuredPerson)
  const pnameof = useNameof<PersonAdditionalData>(personPrefix)

  const { data: dataTitles } = useSelections(SelectionType.TitleBefore)

  const insuredSameAs = watch('policyHolder.insuredPersonSameAs')
  const pType = watch('policyHolder.insuredPerson.type')

  const sameAsOptions = [
    { label: 'Stejný s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    { label: 'Jiný', value: null },
  ]

  // nastavení default typu osoby pri prekliknuti
  useEffect(() => {
    if (!insuredSameAs && !pType)
      setValue('policyHolder.insuredPerson.type', Selection.Person)
  }, [insuredSameAs])

  const { error: agreementError } = getFieldState('policyHolder.insuredPerson.agreement')
  const titleAgreement = 'Souhlas s elektronickou komunikací'

  return (
    <FormBody>
      <FormControl>
        <RadioGroup
          value={insuredSameAs?.toString() ?? '0'}
          name="policyHolder.insuredPersonSameAs"
          isDisabled
          onChange={(value) => setValue('policyHolder.insuredPersonSameAs', parseInt(value))}
        >
          <Stack direction="row" spacing={16}>
            {sameAsOptions?.map((item) => (
              <Radio key={item.value ?? -1} value={item.value?.toString() ?? '0'}>
                {item.label}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>

      {!insuredSameAs &&
        <>
          <PersonType personPrefix={personPrefix} disabled />

          {isIn(pType, Selection.Company, Selection.SelfEmployed) &&
            <ICOOptions personPrefix={personPrefix} disabled hideIc />
          }

          {isIn(pType, Selection.Person, Selection.SelfEmployed) &&
            <FormRow itemsCount={3}>
              <FormControl>
                <FormLabel>Titul před jménem</FormLabel>

                {(dataTitles?.data.length ?? 0) > 0 &&
                  <Select
                    {...register('policyHolder.insuredPerson.titleBefore')}
                    isDisabled={isDisabled}
                    defaultValue={getValues('policyHolder.insuredPerson.titleBefore') ?? ''}
                  >
                    <option value=''>Vyberte</option>
                    {dataTitles?.data.map((item) => (
                      <option value={item.value?.toString()} key={item.id}>{item.value}</option>
                    ))}
                  </Select>
                }
              </FormControl>

              <InsureeName personPrefix={personPrefix} disabled />
            </FormRow>
          }

          
          <RuianAddressSelector
            addressPath={pnameof((x) => x.address)}
            isDisabled={isDisabled || product == Product.LiabilityInsurance}
            showOnlyLotNumber={false}
          />

          <InsureeContact personPrefix={personPrefix} isDisabled={isDisabled} />

              <FormControl isInvalid={!!agreementError?.message}>
                <Flex sx={{ gap: 2 }}>
                  <Checkbox
                    {...register('policyHolder.insuredPerson.agreement')}
                    isDisabled={isDisabled}
                  >
                    {titleAgreement}
                  </Checkbox>
                  <CustomTooltip
                    trigger="clickHover"
                    hasArrow
                    label={(
                      <Box>
                        <Text>
                          Klient souhlasí a požaduje odesílání informací a dokumentů elektronicky (e-mailem příp. SMS).
                        </Text>
                      </Box>
                    )}
                    placement="top"
                  >
                    <QuestionMarkIcon />
                  </CustomTooltip>
                </Flex>

            {<FormErrorMessage>{agreementError?.message}</FormErrorMessage>}
          </FormControl>

        </>
      }

    </FormBody>
  )
}